import { useState, useRef } from "react";
import axios from "axios";

const VoiceUploader = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("");
  const [voiceName, setVoiceName] = useState("MyVoice");
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const BACKEND_URL = `${process.env.REACT_APP_BACKEND_URL}/integration/clone-voice/`;

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
        setFile(new File([audioBlob], "recorded_audio.webm", { type: "audio/webm" }));
        setStatus("Recording stopped. Ready to upload.");
      };

      mediaRecorder.start();
      setIsRecording(true);
      setStatus("Recording...");
    } catch (err) {
      console.error(err);
      setStatus("Microphone access denied.");
    }
  };

  // ⏹️ Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // 📁 Handle manual file upload
  const handleFileChange = (e) => {
    const uploadedFile = e.target.files[0];
    setFile(uploadedFile);
    setAudioUrl(URL.createObjectURL(uploadedFile));
  };

  // 📤 Upload to Django backend
    const handleUpload = async () => {
    if (!file) {
        alert("Please record or upload a file first!");
        return;
    }

    setStatus("Uploading to backend...");

    const formData = new FormData();
    formData.append("name", voiceName);
    formData.append("file", file);

    try {
        const response = await axios.post(`${BACKEND_URL}`, formData, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
        });

        console.log(response.data);

        setStatus(`✅ Voice uploaded successfully! Voice ID: ${response.data.voice_id || "N/A"}`);
    } catch (error) {
        console.error("Upload error:", error);

        if (error.response) {
        // Server responded with a non-2xx status
        setStatus(`❌ Upload failed: ${error.response.data.error || error.message}`);
        } else if (error.request) {
        // No response received
        setStatus("❌ No response from server.");
        } else {
        // Other errors
        setStatus(`❌ Error: ${error.message}`);
        }
    }
    };

  return (
    <div className="flex flex-col items-center p-6 bg-gray-100 rounded-2xl shadow-lg max-w-md mx-auto space-y-4">
      <h2 className="text-2xl font-bold">🎙️ Voice Upload or Record</h2>

      <input
        type="text"
        value={voiceName}
        onChange={(e) => setVoiceName(e.target.value)}
        placeholder="Enter voice name"
        className="border border-gray-300 rounded-lg px-3 py-2 w-full"
      />

      {/* Recording Section */}
      <div className="flex gap-4">
        {!isRecording ? (
          <button
            onClick={startRecording}
            className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600"
          >
            Start Recording
          </button>
        ) : (
          <button
            onClick={stopRecording}
            className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600"
          >
            Stop Recording
          </button>
        )}
      </div>

      {/* File Upload Section */}
      <div className="flex flex-col items-center space-y-2">
        <p className="font-semibold">Or upload a file:</p>
        <input type="file" accept="audio/*" onChange={handleFileChange} />
      </div>

      {audioUrl && (
        <div className="w-full">
          <audio src={audioUrl} controls className="w-full mt-4" />
        </div>
      )}

      <button
        onClick={handleUpload}
        disabled={!file}
        className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:bg-gray-400"
      >
        Clone Voice
      </button>

      <p className="text-gray-700 mt-3">{status}</p>
    </div>
  );
};

export default VoiceUploader;
